import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, PackageX, Download, FileText, FileSpreadsheet } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const RAISONS = [
  "Cassé",
  "Perdu",
  "Volé",
  "Défaut de fabrication",
  "Retour client endommagé",
  "Autre"
];

const Pertes = () => {
  const { token } = useAuth();
  const [pertes, setPertes] = useState([]);
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    produit_id: "",
    quantite: 1,
    raison: "",
    commentaire: "",
    date: new Date().toISOString().slice(0, 10)
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [pertesRes, produitsRes] = await Promise.all([
        axios.get(`${API}/pertes`, { headers }),
        axios.get(`${API}/produits`, { headers })
      ]);
      setPertes(pertesRes.data);
      setProduits(produitsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.produit_id || !formData.raison) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    try {
      await axios.post(`${API}/pertes`, {
        ...formData,
        quantite: parseInt(formData.quantite)
      }, { headers });
      toast.success("Perte enregistrée");
      setDialogOpen(false);
      setFormData({
        produit_id: "",
        quantite: 1,
        raison: "",
        commentaire: "",
        date: new Date().toISOString().slice(0, 10)
      });
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette perte ? Le stock sera restauré.")) return;
    
    try {
      await axios.delete(`${API}/pertes/${id}`, { headers });
      toast.success("Perte supprimée, stock restauré");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      const endpoint = format === 'pdf' ? `${API}/export/pertes/pdf` : `${API}/export/pertes`;
      const response = await axios.get(endpoint, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pertes_topcocoon.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export ${format.toUpperCase()} téléchargé !`);
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const selectedProduit = produits.find(p => p.id === formData.produit_id);
  const totalPertes = pertes.reduce((acc, p) => acc + p.cout_perte, 0);
  const totalUnites = pertes.reduce((acc, p) => acc + p.quantite, 0);

  // Group products by model
  const productsByModel = produits.reduce((acc, p) => {
    if (!acc[p.modele]) acc[p.modele] = [];
    acc[p.modele].push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pertes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Pertes</h1>
          <p className="text-slate-500 mt-1">Enregistrement des produits cassés, perdus ou défectueux</p>
        </div>
        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-pertes-btn">
                <Download className="w-4 h-4 mr-2" />
                Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-pertes-csv-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                Exporter en CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pertes-pdf-btn">
                <FileText className="w-4 h-4 mr-2 text-red-600" />
                Exporter en PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-700" data-testid="add-perte-btn">
              <Plus className="w-4 h-4 mr-2" />
              Déclarer une perte
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="perte-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PackageX className="w-5 h-5 text-red-600" />
                Déclarer une perte
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Produit *</Label>
                <Select
                  value={formData.produit_id}
                  onValueChange={(value) => setFormData({ ...formData, produit_id: value })}
                >
                  <SelectTrigger data-testid="perte-produit-select">
                    <SelectValue placeholder="Sélectionner un produit" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(productsByModel).map(([modele, prods]) => (
                      <div key={modele}>
                        <div className="px-2 py-1.5 text-sm font-semibold text-slate-500 bg-slate-100">
                          {modele}
                        </div>
                        {prods.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.couleur} - Stock: {p.stock_restant}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
                {selectedProduit && (
                  <p className="text-xs text-slate-500">
                    SKU: {selectedProduit.sku} | Coût: {selectedProduit.cost_unit}€/unité
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantité *</Label>
                  <Input
                    type="number"
                    min="1"
                    max={selectedProduit?.stock_restant || 999}
                    value={formData.quantite}
                    onChange={(e) => setFormData({ ...formData, quantite: e.target.value })}
                    required
                    data-testid="perte-quantite-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    data-testid="perte-date-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Raison *</Label>
                <Select
                  value={formData.raison}
                  onValueChange={(value) => setFormData({ ...formData, raison: value })}
                >
                  <SelectTrigger data-testid="perte-raison-select">
                    <SelectValue placeholder="Sélectionner une raison" />
                  </SelectTrigger>
                  <SelectContent>
                    {RAISONS.map((raison) => (
                      <SelectItem key={raison} value={raison}>{raison}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Commentaire (optionnel)</Label>
                <Input
                  value={formData.commentaire}
                  onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                  placeholder="Détails supplémentaires..."
                  data-testid="perte-commentaire-input"
                />
              </div>

              {selectedProduit && formData.quantite > 0 && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-700">
                    <strong>Coût de la perte:</strong> {(selectedProduit.cost_unit * formData.quantite).toFixed(2)}€
                  </p>
                </div>
              )}

              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" data-testid="submit-perte-btn">
                <PackageX className="w-4 h-4 mr-2" />
                Enregistrer la perte
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <p className="text-sm text-red-600">Coût total des pertes</p>
            <p className="text-3xl font-bold text-red-700">{totalPertes.toFixed(2)} €</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-slate-500">Unités perdues</p>
            <p className="text-3xl font-bold text-slate-800">{totalUnites}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-slate-500">Incidents déclarés</p>
            <p className="text-3xl font-bold text-slate-800">{pertes.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card data-testid="pertes-table-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Historique des pertes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          ) : pertes.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <PackageX className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune perte enregistrée</p>
              <p className="text-sm mt-1">C'est une bonne nouvelle !</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Modèle</TableHead>
                    <TableHead>Couleur</TableHead>
                    <TableHead className="text-right">Quantité</TableHead>
                    <TableHead>Raison</TableHead>
                    <TableHead>Commentaire</TableHead>
                    <TableHead className="text-right">Coût</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pertes.map((perte) => (
                    <TableRow key={perte.id} data-testid={`perte-row-${perte.id}`}>
                      <TableCell className="font-medium">{perte.date}</TableCell>
                      <TableCell className="font-mono text-sm">{perte.sku}</TableCell>
                      <TableCell>{perte.modele}</TableCell>
                      <TableCell>{perte.couleur}</TableCell>
                      <TableCell className="text-right font-semibold">{perte.quantite}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          {perte.raison}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-32 truncate text-slate-500">
                        {perte.commentaire || "-"}
                      </TableCell>
                      <TableCell className="text-right font-medium text-red-600">
                        -{perte.cout_perte.toFixed(2)} €
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(perte.id)}
                          className="text-slate-500 hover:text-slate-700"
                          data-testid={`delete-perte-${perte.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Pertes;
